import { useEffect, useReducer } from 'react';
import { Form, Nav } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SITE_NAME } from '../../constants/siteSeo';
import {
  isSampleLocalPreferred,
  subscribeSampleDataSourcePreference,
} from '../../services/systems/sample-local/sampleLocalMode';
import { useAuth, isAdminUser } from '../../hooks/useAuth';
import { useAdminStorageViewAll } from '../../hooks/useAdminStorageViewAll';
import { useSampleStorageSetupGate } from '../../hooks/useSampleStorageSetupGate';
import { resolveSampleManagerNavHref } from '../../utils/sampleStorageSetupGate';
import styles from './SampleManagerNavigation.module.css';
import './sampleIcons.css';

/** DB 専用（ローカル Zip 正本希望中は出さない）— Excel 入出力。帳票は Local 対応済み */
const DB_ONLY_NAV_PATHS = new Set<string>(['/excel-import']);

/** Layout の SectionSelect 等、shell 外でも preference 変更で再描画する */
function useSampleDataSourcePreferenceTick(): void {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => subscribeSampleDataSourcePreference(bump), []);
}

export const SAMPLE_MANAGER_NAV_ITEMS = [
  {
    path: '/samples/dashboard',
    labelKey: 'sample.dashboard.nav',
    shortLabelKey: 'sample.dashboard.nav_short',
    defaultLabel: 'Overview',
    defaultShortLabel: 'Overview',
  },
  {
    path: '/samples',
    // labelKey=フル名（title）、shortLabelKey=タブ可視（「一覧」）
    labelKey: 'navigation.sample_management',
    shortLabelKey: 'navigation.sample_management_short',
    defaultLabel: 'Sample list',
    defaultShortLabel: 'List',
  },
  {
    path: '/samples/cases',
    labelKey: 'sample.case_manager.nav',
    shortLabelKey: 'sample.case_manager.nav_short',
    defaultLabel: 'Cases',
    defaultShortLabel: 'Cases',
  },
  {
    path: '/reports',
    labelKey: 'navigation.reports',
    shortLabelKey: 'navigation.reports_short',
    defaultLabel: 'Reports',
    defaultShortLabel: 'Reports',
  },
  {
    path: '/samples/usage-records',
    labelKey: 'navigation.usage_records',
    shortLabelKey: 'navigation.usage_records_short',
    defaultLabel: 'Usage',
    defaultShortLabel: 'Usage',
  },
  {
    path: '/storage-rooms',
    labelKey: 'navigation.storage_room_management',
    shortLabelKey: 'navigation.storage_room_management_short',
    defaultLabel: 'Storage',
    defaultShortLabel: 'Storage',
  },
  {
    path: '/excel-import',
    // 可視・document.title は短名、title 属性はフル説明
    labelKey: 'excel_import.title_full',
    shortLabelKey: 'navigation.excel_import_short',
    defaultLabel: 'Excel import and export',
    defaultShortLabel: 'Excel',
  },
] as const;

export type SampleManagerNavPath = (typeof SAMPLE_MANAGER_NAV_ITEMS)[number]['path'];

export function getVisibleSampleManagerNavItems() {
  if (!isSampleLocalPreferred()) return SAMPLE_MANAGER_NAV_ITEMS;
  return SAMPLE_MANAGER_NAV_ITEMS.filter((item) => !DB_ONLY_NAV_PATHS.has(item.path));
}

function isNavActive(pathname: string, itemPath: string): boolean {
  if (itemPath === '/samples') {
    // 詳細・使用履歴は URL に ID を載せないため、一覧タブ配下として扱う
    return (
      pathname === '/samples' ||
      pathname === '/samples/detail' ||
      pathname === '/samples/usage-history' ||
      /^\/samples\/\d+(\/|$)/.test(pathname)
    );
  }
  // 選択エクスポートは入出力タブの兄弟ページ（ナビ項目は増やさない）
  if (itemPath === '/excel-import') {
    return (
      pathname === '/excel-import' ||
      pathname === '/excel-export' ||
      pathname.startsWith('/excel-import/')
    );
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

/** Layout の dense nav / フッター圧縮など、サンプル管理系パス判定の単一の源 */
export function isSampleManagerPathname(pathname: string): boolean {
  if (pathname === '/excel-export' || pathname.startsWith('/excel-export/')) {
    return true;
  }
  return SAMPLE_MANAGER_NAV_ITEMS.some((item) => isNavActive(pathname, item.path));
}

export function resolveSampleManagerNavPath(pathname: string): SampleManagerNavPath {
  if (pathname === '/excel-export' || pathname.startsWith('/excel-export/')) {
    return '/excel-import';
  }
  return (
    SAMPLE_MANAGER_NAV_ITEMS.find((item) => isNavActive(pathname, item.path))?.path ?? '/samples'
  );
}

/** 詳細・個別使用履歴（ID を URL に載せない面） */
export function isSampleDetailSurfacePathname(pathname: string): boolean {
  return (
    pathname === '/samples/detail' ||
    pathname === '/samples/usage-history' ||
    /^\/samples\/\d+(\/|$)/.test(pathname)
  );
}

const DETAIL_SECTION_SELECT_VALUE = '/samples/detail';
const USAGE_HISTORY_SECTION_SELECT_VALUE = '/samples/usage-history';

function isStickySectionSelectValue(value: string): boolean {
  return value === DETAIL_SECTION_SELECT_VALUE || value === USAGE_HISTORY_SECTION_SELECT_VALUE;
}

/** xs セクション select の value（詳細／個別使用履歴は一覧に潰さない） */
export function resolveSampleManagerSectionSelectValue(pathname: string): string {
  if (pathname === '/samples/usage-history') return USAGE_HISTORY_SECTION_SELECT_VALUE;
  if (isSampleDetailSurfacePathname(pathname)) return DETAIL_SECTION_SELECT_VALUE;
  return resolveSampleManagerNavPath(pathname);
}

/**
 * ブラウザタブ用タイトル。
 * 詳細／個別使用履歴の未選択時は短名。サンプル名付きは各ページが上書き。
 * 例: 「一覧 · kensuke-system」
 */
export function resolveSampleManagerDocumentTitle(
  pathname: string,
  t: (key: string, options?: { defaultValue?: string }) => string,
  siteName: string = SITE_NAME
): string | null {
  if (!isSampleManagerPathname(pathname)) {
    return null;
  }
  if (pathname === '/samples/usage-history') {
    return `${t('usage_history.nav_short', { defaultValue: '使用履歴' })} · ${siteName}`;
  }
  if (pathname === '/excel-export' || pathname.startsWith('/excel-export/')) {
    // ナビは入出力タブ配下だが、タブタイトルは選択出力を明示
    return `${t('excel_export.nav_short', { defaultValue: '選択出力' })} · ${siteName}`;
  }
  if (isSampleDetailSurfacePathname(pathname)) {
    return `${t('sample.detail_nav_short', { defaultValue: '詳細' })} · ${siteName}`;
  }
  const navPath = resolveSampleManagerNavPath(pathname);
  const item = SAMPLE_MANAGER_NAV_ITEMS.find((entry) => entry.path === navPath);
  const pageTitle = item
    ? t(item.shortLabelKey, { defaultValue: item.defaultShortLabel })
    : t('navigation.menu_sample', { defaultValue: 'サンプル管理' });
  return `${pageTitle} · ${siteName}`;
}

function useSampleManagerDocumentTitle(): string | null {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  void i18n.language;
  return resolveSampleManagerDocumentTitle(pathname, t);
}

/**
 * xs: サイトナビ内に置くセクション切替（タブバー二重ナビを解消）。
 */
export function SampleManagerSectionSelect({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  useSampleDataSourcePreferenceTick();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { adminViewAll } = useAdminStorageViewAll(isAdmin);
  const storageSetupGate = useSampleStorageSetupGate(adminViewAll);
  const onDetailSurface = isSampleDetailSurfacePathname(location.pathname);
  const value = resolveSampleManagerSectionSelectValue(location.pathname);

  return (
    <Form.Select
      size="sm"
      className={className}
      aria-label={t('navigation.menu_sample', { defaultValue: 'サンプル管理' })}
      title={t('navigation.sample_section_select_title', {
        defaultValue: 'サンプル管理の画面を切り替え',
      })}
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        if (isStickySectionSelectValue(next)) return;
        navigate(
          resolveSampleManagerNavHref(next, {
            isReady: storageSetupGate.isReady,
            hasStorageRooms: storageSetupGate.hasStorageRooms,
          })
        );
      }}
    >
      {onDetailSurface ? (
        <option value={value}>
          {value === USAGE_HISTORY_SECTION_SELECT_VALUE
            ? t('usage_history.nav_short', { defaultValue: '使用履歴' })
            : t('sample.detail_nav_short', { defaultValue: '詳細' })}
        </option>
      ) : null}
      {getVisibleSampleManagerNavItems().map((item) => (
        <option key={item.path} value={item.path}>
          {t(item.shortLabelKey, { defaultValue: item.defaultShortLabel })}
        </option>
      ))}
    </Form.Select>
  );
}

/**
 * サンプル管理システム内のページ間を移動するためのタブナビ（md 以上）。
 * xs は Layout の SampleManagerSectionSelect が担当する。
 */
export default function SampleManagerNavigation() {
  const { t } = useTranslation();
  const location = useLocation();
  const documentTitle = useSampleManagerDocumentTitle();
  useSampleDataSourcePreferenceTick();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { adminViewAll } = useAdminStorageViewAll(isAdmin);
  const storageSetupGate = useSampleStorageSetupGate(adminViewAll);

  if (!isSampleManagerPathname(location.pathname)) {
    return null;
  }

  return (
    <>
      {documentTitle ? (
        <Helmet>
          <title>{documentTitle}</title>
        </Helmet>
      ) : null}
      <div className={`${styles.navContainer} d-none d-md-block`}>
        <Nav variant="tabs" className="mb-0">
          {getVisibleSampleManagerNavItems().map((item) => {
            const active = isNavActive(location.pathname, item.path);
            // 可視は常に短名（xs SectionSelect と同じ）。title にフル名（短いときは省略）
            const shortLabel = t(item.shortLabelKey, { defaultValue: item.defaultShortLabel });
            const fullLabel = t(item.labelKey, { defaultValue: item.defaultLabel });
            const to = resolveSampleManagerNavHref(item.path, {
              isReady: storageSetupGate.isReady,
              hasStorageRooms: storageSetupGate.hasStorageRooms,
            });
            return (
              <Nav.Item key={item.path}>
                <Nav.Link
                  as={Link}
                  to={to}
                  active={active}
                  className={`${styles.navLink}${active ? ` ${styles.navLinkActive}` : ''}`}
                  title={fullLabel !== shortLabel ? fullLabel : undefined}
                >
                  {shortLabel}
                </Nav.Link>
              </Nav.Item>
            );
          })}
        </Nav>
      </div>
    </>
  );
}
